import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { usePostApiWorkspaces, useGetApiWorkspaces } from '@/api/generated/workspaces/workspaces';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const createWorkspaceSchema = z.object({
  name: z.string().min(3, 'O nome deve ter no mínimo 3 caracteres'),
});

type CreateWorkspaceFormValues = z.infer<typeof createWorkspaceSchema>;

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({ open, onOpenChange }) => {
  const createMutation = usePostApiWorkspaces();
  const { setWorkspaces, setWorkspace } = useWorkspaceStore();
  const { refetch } = useGetApiWorkspaces();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateWorkspaceFormValues>({
    resolver: zodResolver(createWorkspaceSchema),
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(); // clear form on close
    }
    onOpenChange(newOpen);
  };

  const onSubmit = async (data: CreateWorkspaceFormValues) => {
    try {
      const result = await createMutation.mutateAsync({ data });
      toast.success('Workspace criado com sucesso!');
      
      const { data: workspacesList } = await refetch();
      if (workspacesList) {
        setWorkspaces(workspacesList as any[]);
        
        const newWorkspace = (result as any)?.id 
          ? (workspacesList as any[]).find((w: any) => w.id === (result as any).id)
          : (workspacesList as any[]).find((w: any) => w.name === data.name);
          
        if (newWorkspace) {
          setWorkspace(newWorkspace.id);
        }
      }

      handleOpenChange(false);
    } catch (error) {
      // API client interceptor usually handles error toasts
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Criar Workspace</DialogTitle>
          <DialogDescription>
            Dê um nome ao seu novo espaço de trabalho para gerenciar seu time e lojas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Workspace</Label>
            <Input
              id="name"
              placeholder="Ex: Minha Empresa"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Criando...' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
